import { create } from 'zustand';
import { User, RestaurantProfile } from '../types/api';

interface AuthState {
  // User and profile data
  user: User | null;
  profile: RestaurantProfile | null;

  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;

  // Error handling
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setProfile: (profile: RestaurantProfile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  // Initial state
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  // Actions
  setUser: (user) => {
    set({
      user,
      isAuthenticated: !!user,
      error: null, // Clear any previous errors when setting user
    });
  },

  setProfile: (profile) => {
    set({
      profile,
      error: null, // Clear any previous errors when setting profile
    });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  logout: () => {
    set({
      user: null,
      profile: null,
      isAuthenticated: false,
      error: null,
      isLoading: false,
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Selector hooks for specific pieces of state
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthProfile = () => useAuthStore((state) => state.profile);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
